import { forwardRef, type TextareaHTMLAttributes, useState } from 'react';
import { Icon } from '../icon/icon';
import { Textarea as ShadcnTextarea } from '@workspace/ui/components/textarea';
import * as styles from './textarea.styles';
import { cn } from '@workspace/ui/lib/utils';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  helperText?: string;
  errorText?: string;
  maxLine?: number;
  count?: {
    show: boolean;
    maxLength: number;
  };
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ helperText, errorText, count, maxLine, ...props }, ref) => {
    const hasError = Boolean(errorText);

    const [text, setText] = useState('');

    const containerStyle = cn(styles.container.base);
    const shadcnTextareaStyle = cn(styles.textarea.base, hasError && styles.textarea.error);
    const bottomAreaStyle = cn(styles.bottomArea.base, hasError && styles.bottomArea.error);
    const helperTextStyle = cn(styles.helperText.base);
    const countStyle = cn(styles.count.base);

    return (
      <div>
        <div className={containerStyle}>
          <ShadcnTextarea
            ref={ref}
            className={shadcnTextareaStyle}
            value={text}
            {...props}
            onKeyDown={(e) => {
              if (!(e.target instanceof HTMLTextAreaElement)) return;

              const lines = e.target.value.split('\n').length;
              if (e.key === 'Enter') {
                if (lines === maxLine) {
                  e.preventDefault();
                }
              }
            }}
            onChange={(e) => {
              let nextText = e.target.value;
              if (count && e.target.value.length > count.maxLength) {
                nextText = e.target.value.slice(0, count.maxLength);
                e.target.value = nextText;
              }
              props.onChange?.(e);
              setText(e.target.value);
            }}
          />
        </div>
        {(helperText || hasError || count?.show) && (
          <div className={bottomAreaStyle}>
            <div className={helperTextStyle}>
              {hasError && <Icon name='ExclamationMarkFilled' size={16} color='inherit' />}
              {errorText || helperText}
            </div>
            {count?.show && (
              <div className={countStyle}>
                {text.length} / {count.maxLength}
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';

export { Textarea };
